import { useState } from 'react';
import {
  formatMm,
  getEndValue,
  LINK_PROPERTY_LABEL,
  makeLink,
  propagateLinks,
  shelfOfPart,
  type CabinetLinkProperty,
  type ShelfLinkProperty,
  type EvaluatedCabinet,
  type LinkEnd,
  type PanelLinkProperty,
  type ProjectDocument,
} from '@scs/domain-engine';
import type { EditorStore, Selection } from './store';
import { panelKeyOfPart } from './AssemblyProperties';
import { partWorldBox, unitWorldBox } from './snap';
import type { LinkLine } from './viewport';

export interface LinkItem {
  key: string;
  label: string;
  kind: 'panel' | 'cabinet' | 'shelves';
  unitId: string;
  panelKey?: string;
  setId?: string;
}

/** Linkable things in the selection: assembly panels (Ctrl+click) and/or whole parametric cabinets (Shift+click). */
export function linkCandidates(doc: ProjectDocument, sel: Selection): LinkItem[] {
  const items: LinkItem[] = [];
  const seen = new Set<string>();
  for (const pid of [sel.partId, ...(sel.extraPartIds ?? [])]) {
    if (!pid) continue;
    const [unitId, localKey] = pid.split('/') as [string, string];
    const asm = doc.assemblies.find((a) => a.id === unitId);
    // A shelf stands for its whole shelf set.
    const shelf = asm && shelfOfPart(asm, localKey);
    if (shelf) {
      if (seen.has(`s:${unitId}/${shelf.set.id}`)) continue;
      seen.add(`s:${unitId}/${shelf.set.id}`);
      items.push({ key: `s:${unitId}/${shelf.set.id}`, label: `${asm!.name}: ${shelf.set.label}`, kind: 'shelves', unitId, setId: shelf.set.id });
      continue;
    }
    const key = asm && panelKeyOfPart(asm, localKey);
    if (!asm || !key || seen.has(`${unitId}/${key}`)) continue;
    seen.add(`${unitId}/${key}`);
    items.push({ key: `p:${unitId}/${key}`, label: `${asm.name}: ${asm.panels.find((p) => p.key === key)!.label}`, kind: 'panel', unitId, panelKey: key });
  }
  if (sel.shelfSetId && sel.cabinetIds.length === 1) {
    const asm = doc.assemblies.find((a) => a.id === sel.cabinetIds[0]);
    const set = asm?.shelfSets?.find((s) => s.id === sel.shelfSetId);
    if (asm && set && !seen.has(`s:${asm.id}/${set.id}`)) items.push({ key: `s:${asm.id}/${set.id}`, label: `${asm.name}: ${set.label}`, kind: 'shelves', unitId: asm.id, setId: set.id });
  }
  const partUnits = new Set(items.map((i) => i.unitId));
  for (const id of sel.cabinetIds) {
    const c = doc.cabinets.find((x) => x.id === id);
    if (c && !partUnits.has(id)) items.push({ key: `c:${id}`, label: c.name, kind: 'cabinet', unitId: id });
  }
  return items;
}

const PANEL_PROPS: PanelLinkProperty[] = ['length', 'width', 'thickness'];
const CAB_PROPS: CabinetLinkProperty[] = ['width', 'height', 'depth'];
export const SHELF_PROPS: ShelfLinkProperty[] = ['count', 'gap', 'thickness'];
const PROPS = { panel: PANEL_PROPS, cabinet: CAB_PROPS, shelves: SHELF_PROPS } as const;

function toEnd(item: LinkItem, property: string): LinkEnd {
  if (item.kind === 'panel') return { kind: 'panel', unitId: item.unitId, panelKey: item.panelKey!, property: property as PanelLinkProperty };
  if (item.kind === 'shelves') return { kind: 'shelves', unitId: item.unitId, setId: item.setId!, property: property as ShelfLinkProperty };
  return { kind: 'cabinet', unitId: item.unitId, property: property as CabinetLinkProperty };
}

/** Value with its unit (shelf count has none). */
const show = (property: string, v: number) => (property === 'count' ? String(v) : `${formatMm(v)} mm`);

/** Links the first item's properties to all others ("equal"), one undo step. */
export function linkEqual(exec: EditorStore['exec'], items: LinkItem[], properties: string[], label: string) {
  exec(label, (d) => {
    const stamp = Date.now().toString(36);
    let n = 0;
    for (const property of properties) {
      const master = toEnd(items[0]!, property);
      for (let i = 1; i < items.length; i++) {
        const other = toEnd(items[i]!, property);
        if (d.links.some((l) => (endKeyOf(l.a) === endKeyOf(master) && endKeyOf(l.b) === endKeyOf(other)) || (endKeyOf(l.b) === endKeyOf(master) && endKeyOf(l.a) === endKeyOf(other)))) continue;
        d.links.push(makeLink(d, `ln${stamp}${n++}`, master, other, 'equal'));
      }
      propagateLinks(d, master);
    }
  });
}
const endKeyOf = (e: LinkEnd) => JSON.stringify(e);

export function endLabel(doc: ProjectDocument, e: LinkEnd): string {
  if (e.kind === 'panel') {
    const a = doc.assemblies.find((x) => x.id === e.unitId);
    return `${a?.panels.find((p) => p.key === e.panelKey)?.label ?? e.panelKey} (${LINK_PROPERTY_LABEL[e.property].toLowerCase()})`;
  }
  if (e.kind === 'shelves') {
    const a = doc.assemblies.find((x) => x.id === e.unitId);
    return `${a?.shelfSets?.find((s) => s.id === e.setId)?.label ?? e.setId} (${LINK_PROPERTY_LABEL[e.property].toLowerCase()})`;
  }
  return `${doc.cabinets.find((c) => c.id === e.unitId)?.name ?? e.unitId} (${LINK_PROPERTY_LABEL[e.property].toLowerCase()})`;
}

export function LinkDialog({ doc, items, exec, onClose }: { doc: ProjectDocument; items: LinkItem[]; exec: EditorStore['exec']; onClose(): void }) {
  const [props, setProps] = useState<string[]>(() => items.map((i) => (i.kind === 'panel' ? 'length' : i.kind === 'shelves' ? 'thickness' : 'height')));
  const allShelves = items.every((i) => i.kind === 'shelves');
  const [mode, setMode] = useState<'equal' | 'offset'>('equal');
  const values = items.map((it, i) => getEndValue(doc, toEnd(it, props[i]!)) ?? 0);
  const create = () => {
    exec(mode === 'equal' ? 'Link' : 'Link sa razlikom', (d) => {
      const master = toEnd(items[0]!, props[0]!);
      const stamp = Date.now().toString(36);
      for (let i = 1; i < items.length; i++) d.links.push(makeLink(d, `ln${stamp}${i}`, master, toEnd(items[i]!, props[i]!), mode));
      // "Link" makes the others equal to the first item right away; "keep difference" changes nothing now.
      if (mode === 'equal') propagateLinks(d, master);
    });
    onClose();
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal wide" role="dialog" aria-modal aria-label="Link dimenzija" onClick={(e) => e.stopPropagation()}>
        <h2>Link dimenzija</h2>
        <p className="muted">Prvi element je glavni. Kad se bilo koja linkovana mjera promijeni, ostale je prate.</p>
        <table className="grid">
          <thead>
            <tr>
              <th>Element</th>
              <th>Mjera</th>
              <th>Sada</th>
              <th>{mode === 'offset' ? 'Razlika prema prvom' : 'Nakon linka'}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.key}>
                <td>
                  {i === 0 ? '★ ' : ''}
                  {it.label}
                </td>
                <td>
                  <select className="input" style={{ height: 24 }} value={props[i]} onChange={(e) => setProps(props.map((p, j) => (j === i ? e.target.value : p)))}>
                    {PROPS[it.kind].map((p) => (
                      <option key={p} value={p}>
                        {LINK_PROPERTY_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="n">{show(props[i]!, values[i]!)}</td>
                <td className="n">
                  {i === 0 ? '—' : mode === 'offset' ? `${values[i]! - values[0]! >= 0 ? '+' : ''}${show(props[i]!, values[i]! - values[0]!)} (zadržava se)` : show(props[i]!, values[0]!)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="radio" checked={mode === 'equal'} onChange={() => setMode('equal')} />
            <span>
              <i className="link-swatch equal" /> Link (iste mjere)
            </span>
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="radio" checked={mode === 'offset'} onChange={() => setMode('offset')} />
            <span>
              <i className="link-swatch offset" /> Link i zadrži razliku
            </span>
          </label>
        </div>
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Otkaži
          </button>
          {allShelves && (
            <button
              className="btn"
              title="Linkuje broj polica, zazor i debljinu (iste mjere)"
              onClick={() => {
                linkEqual(exec, items, SHELF_PROPS, 'Link police (sve)');
                onClose();
              }}
            >
              Link all
            </button>
          )}
          <button className="btn primary" onClick={create}>
            Napravi link
          </button>
        </div>
      </div>
    </div>
  );
}

/** Links touching the selected unit/panel, with unlink buttons. */
export function LinksSection({ doc, unitId, panelKey, setId, exec, disabled }: { doc: ProjectDocument; unitId: string; panelKey?: string; setId?: string; exec: EditorStore['exec']; disabled: boolean }) {
  const touches = (e: LinkEnd) =>
    e.unitId === unitId && (setId ? e.kind === 'shelves' && e.setId === setId : !panelKey || (e.kind === 'panel' && e.panelKey === panelKey));
  const links = doc.links.filter((l) => touches(l.a) || touches(l.b));
  if (!links.length) return null;
  return (
    <div className="prop-group">
      <h3>Linkovi</h3>
      {links.map((l) => (
        <div key={l.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, padding: '2px 0' }}>
          <i className={`link-swatch ${l.mode}`} />
          <span style={{ flex: 1 }}>
            {endLabel(doc, l.a)} → {endLabel(doc, l.b)}
            {l.mode === 'offset' && <span className="faint"> (razlika {show(l.a.property, l.offset)})</span>}
          </span>
          <button
            className="btn sm ghost danger"
            disabled={disabled}
            title="Ukloni link (mjere ostaju kakve jesu)"
            onClick={() =>
              exec('Ukloni link', (d) => {
                d.links = d.links.filter((x) => x.id !== l.id);
              })
            }
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/** World-space centres for the dashed link lines. */
export function linkLines(doc: ProjectDocument, units: EvaluatedCabinet[]): LinkLine[] {
  const centre = (e: LinkEnd) => {
    const u = units.find((c) => c.instance.id === e.unitId);
    if (!u) return null;
    let box = unitWorldBox(u);
    if (e.kind === 'panel' || e.kind === 'shelves') {
      const k = e.kind === 'panel' ? e.panelKey : e.setId;
      const part = u.result.parts.find((p) => p.localKey === k || p.localKey.startsWith(`${k}-`));
      if (!part) return null;
      box = partWorldBox(u, part);
    }
    return [0, 1, 2].map((k) => Math.round((box.min[k]! + box.max[k]!) / 2)) as [number, number, number];
  };
  const out: LinkLine[] = [];
  for (const l of doc.links) {
    const a = centre(l.a);
    const b = centre(l.b);
    if (a && b) out.push({ from: a, to: b, mode: l.mode });
  }
  return out;
}
